import React, { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Spin } from "antd";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();



const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
const DEFAULT_HEIGHT = 700;
const PAGE_GAP = 16;



type PdfErrorKind =
    | "not_pdf"
    | "network"
    | "corrupted"
    | "password"
    | "unknown";

function classifyError(err: unknown): PdfErrorKind {
    const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
    if (msg.includes("password") || msg.includes("encrypt")) return "password";
    if (msg.includes("invalid pdf") || msg.includes("not a pdf") || msg.includes("magic number"))
        return "not_pdf";
    if (
        msg.includes("fetch") ||
        msg.includes("network") ||
        msg.includes("404") ||
        msg.includes("403") ||
        msg.includes("failed to load")
    )
        return "network";
    if (msg.includes("corrupt") || msg.includes("unexpected") || msg.includes("xref"))
        return "corrupted";
    return "unknown";
}


function looksLikePdf(url: string): boolean {
    try {
        const pathname = new URL(url, window.location.href).pathname.toLowerCase();

        if (url.startsWith("data:application/pdf") || url.startsWith("blob:")) return true;
        if (url.startsWith("data:")) return false;
        const nonPdfExts = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".mp4", ".mp3", ".zip", ".docx", ".xlsx"];
        return !nonPdfExts.some((ext) => pathname.endsWith(ext));
    } catch {
        return true;
    }
}



interface ToolbarButtonProps {
    onClick: () => void;
    disabled?: boolean;
    title?: string;
    children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, disabled, title, children }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
            width: 32,
            height: 32,
            border: "1px solid #3d3d5c",
            borderRadius: 6,
            background: disabled ? "#2a2a3e" : "#3d3d5c",
            color: disabled ? "#555" : "#e0e0f0",
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background 0.15s",
        }}
    >
        {children}
    </button>
);



interface PdfErrorStateProps {
    url: string;
    kind: PdfErrorKind;
    onRequestResume: () => void;
}

const ERROR_META: Record<PdfErrorKind, { icon: string; title: string; body: string }> = {
    not_pdf: {
        icon: "ti-file-unknown",
        title: "Not a PDF file",
        body: "The URL doesn't appear to point to a valid PDF document.",
    },
    network: {
        icon: "ti-wifi-off",
        title: "Could not fetch the file",
        body: "The file may be unavailable, behind a login wall, or blocked by CORS.",
    },
    corrupted: {
        icon: "ti-file-broken",
        title: "PDF is corrupted",
        body: "The file was found but its content could not be parsed.",
    },
    password: {
        icon: "ti-lock",
        title: "PDF is password-protected",
        body: "This document requires a password to open.",
    },
    unknown: {
        icon: "ti-file-x",
        title: "Resume could not be loaded",
        body: "The file may be unavailable or in an unsupported format.",
    },
};

const PdfErrorState: React.FC<PdfErrorStateProps> = ({ url, kind, onRequestResume }) => {
    const { icon, title, body } = ERROR_META[kind];
    return (
        <div
            className="d-flex flex-column align-items-center justify-content-center py-5"
            style={{ minHeight: DEFAULT_HEIGHT }}
        >
            <div
                className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                style={{ width: 80, height: 80, background: "#fee2e2" }}
            >
                <i className={`ti ${icon} text-danger`} style={{ fontSize: 36 }} />
            </div>
            <h6 className="fw-bold text-dark mb-1">{title}</h6>
            <p className="text-muted mb-4" style={{ fontSize: 14, maxWidth: 340, textAlign: "center" }}>
                {body}
            </p>
            <div className="d-flex gap-2 flex-wrap justify-content-center">
                {kind !== "not_pdf" && (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary rounded-pill px-4"
                    >
                        <i className="ti ti-download me-2" style={{ fontSize: 15 }} />
                        Try Direct Download
                    </a>
                )}
                <button className="btn btn-primary rounded-pill px-4" onClick={onRequestResume}>
                    <i className="ti ti-file-upload me-2" style={{ fontSize: 15 }} />
                    Request Resume
                </button>
            </div>
        </div>
    );
};



export interface PdfViewerProps {
    url: string;
    onRequestResume?: () => void;
    height?: number;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
    url,
    onRequestResume = () => { },
    height = DEFAULT_HEIGHT,
}) => {
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<PdfErrorKind | null>(

        !looksLikePdf(url) ? "not_pdf" : null
    );


    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

    const scrollRef = useRef<HTMLDivElement>(null);

    const isProgrammaticScroll = useRef(false);



    useEffect(() => {
        if (numPages === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {

                if (isProgrammaticScroll.current) return;


                let best: IntersectionObserverEntry | null = null;
                for (const entry of entries) {
                    if (!best || entry.intersectionRatio > best.intersectionRatio) {
                        best = entry;
                    }
                }
                if (best && best.intersectionRatio > 0) {
                    const idx = pageRefs.current.indexOf(best.target as HTMLDivElement);
                    if (idx !== -1) setCurrentPage(idx + 1);
                }
            },
            {
                root: scrollRef.current,

                threshold: Array.from({ length: 21 }, (_, i) => i * 0.05),
            }
        );

        pageRefs.current.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [numPages, scale]);



    const scrollToPage = useCallback((page: number) => {
        const el = pageRefs.current[page - 1];
        if (!el) return;

        isProgrammaticScroll.current = true;
        setCurrentPage(page);

        el.scrollIntoView({ behavior: "smooth", block: "start" });


        setTimeout(() => {
            isProgrammaticScroll.current = false;
        }, 700);
    }, []);

    const goToPrev = () => scrollToPage(Math.max(1, currentPage - 1));
    const goToNext = () => scrollToPage(Math.min(numPages, currentPage + 1));



    const canZoomOut = scale > ZOOM_STEPS[0];
    const canZoomIn = scale < ZOOM_STEPS[ZOOM_STEPS.length - 1];

    const zoomIn = () => {
        const nextIdx = ZOOM_STEPS.findIndex((s) => s > scale);
        if (nextIdx !== -1) setScale(ZOOM_STEPS[nextIdx]);
    };

    const zoomOut = () => {
        const prevIdx = [...ZOOM_STEPS].reverse().findIndex((s) => s < scale);
        if (prevIdx !== -1) setScale(ZOOM_STEPS[ZOOM_STEPS.length - 1 - prevIdx]);
    };



    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {

            if (document.activeElement?.tagName === "INPUT") return;
            if (e.key === "ArrowDown" || e.key === "PageDown") goToNext();
            if (e.key === "ArrowUp" || e.key === "PageUp") goToPrev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });



    if (loadError) {
        return <PdfErrorState url={url} kind={loadError} onRequestResume={onRequestResume} />;
    }



    return (
        <div style={{ display: "flex", flexDirection: "column", height, background: "#f0f2f5" }}>


            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 16px",
                    background: "#1e1e2e",
                    gap: 12,
                    flexShrink: 0,
                }}
            >

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ToolbarButton onClick={goToPrev} disabled={currentPage <= 1} title="Previous page (↑)">
                        <i className="ti ti-chevron-left" style={{ fontSize: 16 }} />
                    </ToolbarButton>

                    <div
                        style={{
                            fontSize: 13,
                            color: "#c0c0d8",
                            fontWeight: 500,
                            minWidth: 90,
                            textAlign: "center",
                        }}
                    >
                        {isLoading ? (
                            <span style={{ color: "#555" }}>Loading…</span>
                        ) : (
                            <>
                                <span style={{ color: "#fff", fontWeight: 700 }}>{currentPage}</span>
                                <span style={{ color: "#555" }}> / {numPages}</span>
                            </>
                        )}
                    </div>

                    <ToolbarButton onClick={goToNext} disabled={currentPage >= numPages} title="Next page (↓)">
                        <i className="ti ti-chevron-right" style={{ fontSize: 16 }} />
                    </ToolbarButton>
                </div>


                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ToolbarButton onClick={zoomOut} disabled={!canZoomOut} title="Zoom out">
                        <i className="ti ti-zoom-out" style={{ fontSize: 16 }} />
                    </ToolbarButton>

                    <div
                        style={{
                            fontSize: 12,
                            color: "#a0a0c0",
                            fontWeight: 600,
                            minWidth: 44,
                            textAlign: "center",
                        }}
                    >
                        {Math.round(scale * 100)}%
                    </div>

                    <ToolbarButton onClick={zoomIn} disabled={!canZoomIn} title="Zoom in">
                        <i className="ti ti-zoom-in" style={{ fontSize: 16 }} />
                    </ToolbarButton>

                    <button
                        onClick={() => setScale(1.0)}
                        title="Reset zoom"
                        style={{
                            height: 28,
                            padding: "0 10px",
                            border: "1px solid #3d3d5c",
                            borderRadius: 6,
                            background: scale === 1.0 ? "#4f46e5" : "#3d3d5c",
                            color: "#e0e0f0",
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: 600,
                            transition: "background 0.15s",
                        }}
                    >
                        Reset
                    </button>
                </div>


                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        height: 32,
                        padding: "0 12px",
                        border: "1px solid #3d3d5c",
                        borderRadius: 6,
                        background: "#3d3d5c",
                        color: "#e0e0f0",
                        textDecoration: "none",
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                    }}
                >
                    <i className="ti ti-external-link" style={{ fontSize: 14 }} />
                    Open PDF
                </a>
            </div>


            <div
                ref={scrollRef}
                style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: `${PAGE_GAP}px 16px`,
                    gap: PAGE_GAP,

                    scrollBehavior: "smooth",
                }}
            >
                <Document
                    file={url}
                    onLoadSuccess={({ numPages: n }) => {
                        setNumPages(n);
                        setIsLoading(false);

                        pageRefs.current = Array(n).fill(null);
                    }}
                    onLoadError={(err) => {
                        setLoadError(classifyError(err));
                        setIsLoading(false);
                    }}
                    loading={
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 400,
                                gap: 12,
                            }}
                        >
                            <Spin size="large" />
                            <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Loading PDF…</p>
                        </div>
                    }
                    error={

                        <div style={{ textAlign: "center", color: "#c00", padding: 32 }}>
                            Failed to render PDF.
                        </div>
                    }
                >

                    {Array.from({ length: numPages }, (_, i) => (
                        <div
                            key={i}
                            ref={(el) => { pageRefs.current[i] = el; }}

                            style={{ scrollMarginTop: 12 }}
                        >
                            <Page
                                pageNumber={i + 1}
                                scale={scale}
                                renderTextLayer
                                renderAnnotationLayer
                                canvasBackground="white"
                                className="pdf-page-canvas"
                                onRenderError={(err) => {

                                    console.warn(`Page ${i + 1} render error:`, err);
                                }}
                                loading={
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",

                                            height: Math.round(842 * scale),
                                            width: Math.round(595 * scale),
                                            background: "#fff",
                                            borderRadius: 4,
                                            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
                                        }}
                                    >
                                        <Spin />
                                    </div>
                                }
                            />
                        </div>
                    ))}
                </Document>
            </div>


            {numPages > 1 && numPages <= 20 && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 6,
                        padding: "10px 16px",
                        background: "#1e1e2e",
                        flexShrink: 0,
                    }}
                >
                    {Array.from({ length: numPages }, (_, i) => (
                        <button
                            key={i}
                            onClick={() => scrollToPage(i + 1)}
                            title={`Page ${i + 1}`}
                            style={{
                                width: currentPage === i + 1 ? 20 : 8,
                                height: 8,
                                borderRadius: 4,
                                border: "none",
                                background: currentPage === i + 1 ? "#4f46e5" : "#3d3d5c",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                padding: 0,
                            }}
                        />
                    ))}
                </div>
            )}


            <style>{`
        .pdf-page-canvas canvas {
          border-radius: 4px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.18);
          display: block;
        }
        .react-pdf__Page {
          display: flex;
          justify-content: center;
        }
      `}</style>
        </div>
    );
};

export default PdfViewer;